import { useReducer } from 'react'
import classnames from 'classnames'
import moment from 'moment/moment'
import { CopyToClipboard } from 'react-copy-to-clipboard'

import { formatTimeAgo } from 'src/util/dateFormatters'
import Table from 'src/ui/organisms/Table'
import Tooltip from 'src/ui/atoms/Tooltip'

import useProjectApi, { IProject } from './useProjectApi'

interface ProjectEventsState {
  copiedFrom: string | null
  copiedTo: string | null
}

type ProjectEventsAction =
  | { type: 'copiedFrom'; value: string | null }
  | { type: 'copiedTo'; value: string | null }

const eventsReducer = (state: ProjectEventsState, action: ProjectEventsAction) => {
  if (action.type === 'copiedFrom') {
    return {
      ...state,
      copiedFrom: action.value,
    }
  } else if (action.type === 'copiedTo') {
    return {
      ...state,
      copiedTo: action.value,
    }
  }

  return state
}
const explorerUrl = 'https://etherscan.io'
const ProjectEvents = ({ project }: { project: IProject }) => {
  const { getProjectEvents } = useProjectApi()

  const tokenSymbol = project?.data?.tokenSymbol
  const [state, dispatch] = useReducer(eventsReducer, {
    copiedFrom: null,
    copiedTo: null,
  })

  const onCopied = (action: 'copiedFrom' | 'copiedTo', id: string) => {
    dispatch({ type: action, value: id })
    setTimeout(() => {
      dispatch({ type: action, value: null })
    }, 2000)
  }

  const columns = [
    {
      key: 'txID',
      name: 'Txn Hash',
      render: (data: any) => (
        <a
          target="_blank"
          className="overflow-hidden text-ellipsis whitespace-nowrap text-main-blue"
          href={`${explorerUrl}/tx/${data.hash}`}
          rel="noreferrer">
          {data.hash}
        </a>
      ),
    },
    {
      key: 'age',
      name: 'Age',
      width: '15%',
      render: (data: any) => {
        return (
          <Tooltip tip={moment(data.created).format('YYYY-MM-DD HH:mm:ss')}>
            {formatTimeAgo(data.created)}
          </Tooltip>
        )
      },
    },
    {
      key: 'from',
      name: 'From',
      width: '20%',
      render: (data: any) => (
        <div className="flex gap-1">
          <a
            target="_blank"
            className="overflow-hidden text-ellipsis whitespace-nowrap text-main-blue"
            href={`${explorerUrl}/address/${data.from}`}
            rel="noreferrer">
            {data.from}
          </a>
          <CopyToClipboard text={data.from} onCopy={() => onCopied('copiedFrom', data.from)}>
            <span
              className={classnames('icon-copy cursor-pointer text-sm text-light-gray', {
                'icon-tick !text-green': state.copiedFrom === data.from,
              })}
            />
          </CopyToClipboard>
        </div>
      ),
    },
    {
      key: 'to',
      name: 'To',
      width: '20%',
      render: (data: any) => (
        <div className="flex gap-1">
          <a
            target="_blank"
            className="overflow-hidden text-ellipsis whitespace-nowrap text-main-blue"
            href={`${explorerUrl}/address/${data.to}`}
            rel="noreferrer">
            {data.to}
          </a>
          <CopyToClipboard text={data.to} onCopy={() => onCopied('copiedTo', data.to)}>
            <span
              className={classnames('icon-copy cursor-pointer text-sm text-light-gray', {
                'icon-tick !text-green': state.copiedTo === data.to,
              })}
            />
          </CopyToClipboard>
        </div>
      ),
    },
    {
      key: 'quantity',
      name: 'Quantity',
      width: '15%',
      render: (data: any) => {
        return `${data.amount} ${tokenSymbol}`
      },
    },
  ]

  return (
    <div className="flex flex-col gap-4">
      <h2 className="text-white">Events</h2>
      <Table
        columns={columns}
        itemsPerPage={10}
        fetchData={async (page: number) => {
          const events = await getProjectEvents({
            limit: 10,
            offset: page * 10,
          })

          return {
            data: events?.items,
            totalCount: events?.totalItems,
          }
        }}
      />
    </div>
  )
}

export default ProjectEvents
